/** Per-scene atmosphere recipe. Scenes without an entry (new chapters) get DEFAULT_FX. */
export type ParticleKind = 'embers' | 'dust' | 'sparks' | 'rain' | 'ash' | 'motes';
export type PropAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left' | 'right' | 'center';

export interface PropPlacement {
  id: string;
  anchor: PropAnchor;
  /** fraction of the visible frame width */
  width: number;
}

export interface SceneFx {
  props: PropPlacement[];
  particles: ParticleKind[];
  shafts?: { x: number; angle: number; color?: string } | null;
  fog: number;
  fogColor?: string;
}

const P = {
  ivy: { id: 'fg_ivy', anchor: 'top-left', width: 0.4 } as PropPlacement,
  banner: { id: 'fg_banner', anchor: 'right', width: 0.11 } as PropPlacement,
  bannerL: { id: 'fg_banner', anchor: 'left', width: 0.11 } as PropPlacement,
  candles: { id: 'fg_candles', anchor: 'bottom-left', width: 0.2 } as PropPlacement,
  books: { id: 'fg_books', anchor: 'bottom-right', width: 0.24 } as PropPlacement,
  lantern: { id: 'fg_lantern', anchor: 'top-right', width: 0.09 } as PropPlacement,
  pages: { id: 'fg_pages', anchor: 'center', width: 0.8 } as PropPlacement,
};

export const DEFAULT_FX: SceneFx = { props: [P.candles], particles: ['dust', 'embers'], shafts: { x: 0.3, angle: -0.35 }, fog: 0.5 };

export const SCENE_FX: Record<string, SceneFx> = {
  s01_gate: { props: [P.ivy, P.candles], particles: ['embers', 'dust'], shafts: { x: 0.62, angle: -0.3 }, fog: 0.7 },
  s02_archive: { props: [P.books, P.lantern], particles: ['dust', 'motes'], shafts: { x: 0.35, angle: -0.42 }, fog: 0.35 },
  s03_bard: { props: [P.bannerL, P.candles], particles: ['dust', 'motes'], shafts: { x: 0.72, angle: 0.38, color: '#ffd9a0' }, fog: 0.3 },
  s04_tower: { props: [P.lantern], particles: ['dust', 'embers'], shafts: { x: 0.5, angle: -0.2 }, fog: 0.45 },
  s05_market: { props: [P.banner], particles: ['embers', 'dust'], shafts: null, fog: 0.4 },
  s06_bridge: { props: [P.ivy], particles: ['dust', 'motes'], shafts: { x: 0.25, angle: -0.5 }, fog: 0.95 },
  s07_throne: { props: [P.bannerL, P.candles], particles: ['embers', 'dust'], shafts: { x: 0.55, angle: 0.3 }, fog: 0.35 },
  s08_reveal: { props: [P.pages], particles: ['sparks', 'motes'], shafts: { x: 0.5, angle: 0 }, fog: 0.5 },
  s09_storm: { props: [P.pages], particles: ['rain', 'dust'], shafts: null, fog: 1, fogColor: '#9aa7c7' },
  s10_choice: { props: [P.candles], particles: ['embers', 'motes'], shafts: { x: 0.45, angle: -0.25 }, fog: 0.4 },
  s11_finale: { props: [P.ivy], particles: ['sparks', 'embers'], shafts: { x: 0.7, angle: 0.45 }, fog: 0.6 },
  s12_epilogue: { props: [P.books, P.candles], particles: ['embers', 'dust'], shafts: { x: 0.6, angle: -0.3 }, fog: 0.25 },
  title_keyart: { props: [P.candles, P.books], particles: ['embers', 'sparks', 'dust'], shafts: { x: 0.55, angle: -0.32 }, fog: 0.45 },
  // Book 1 expansion (chapters 2 and 3), from scripts/art/manifest-delta-book1-expansion.json sceneVisualDirection
  s09_oath_protocol: { props: [P.pages, P.candles], particles: ['embers', 'motes'], shafts: { x: 0.4, angle: -0.3 }, fog: 0.45 },
  s10_tide_lock: { props: [P.lantern], particles: ['rain', 'dust'], shafts: null, fog: 0.6, fogColor: '#7fa8b0' },
  s11_flood_shelves: { props: [P.pages, P.books], particles: ['motes', 'dust'], shafts: { x: 0.55, angle: 0.25 }, fog: 0.55, fogColor: '#9fb3c0' },
  s12_scribe_pell: { props: [P.books, P.lantern], particles: ['sparks', 'dust'], shafts: { x: 0.7, angle: 0.3, color: '#cfe8b0' }, fog: 0.3 },
  s13_margin_knight: { props: [P.banner], particles: ['dust', 'motes'], shafts: { x: 0.35, angle: -0.45 }, fog: 0.4 },
  s16_ink_whale: { props: [P.pages], particles: ['rain', 'motes'], shafts: { x: 0.5, angle: 0.1, color: '#9fe0e8' }, fog: 0.7, fogColor: '#6f9aa8' },
  s17_dam_burst: { props: [P.pages, P.candles], particles: ['rain', 'ash'], shafts: null, fog: 0.8, fogColor: '#8e98b8' },
  s19_tribunal: { props: [P.bannerL, P.candles], particles: ['embers', 'dust'], shafts: { x: 0.5, angle: 0 }, fog: 0.5 },
  s20_wax_jury: { props: [P.candles], particles: ['embers', 'dust'], shafts: { x: 0.65, angle: 0.35, color: '#d8f0b0' }, fog: 0.35 },
  s22_vell_appeal: { props: [P.ivy], particles: ['sparks', 'motes'], shafts: { x: 0.5, angle: -0.15, color: '#ffffff' }, fog: 0.25, fogColor: '#e8e4f0' },
  s26_monologue_fight: { props: [P.pages], particles: ['sparks', 'embers'], shafts: { x: 0.45, angle: 0, color: '#fff2d0' }, fog: 0.4 },
};

export function sceneFx(sceneId: string, mood: string): SceneFx {
  const base = SCENE_FX[sceneId] ?? DEFAULT_FX;
  const particles = new Set(base.particles);
  if (mood === 'chaos') particles.add('sparks');
  if (mood === 'dread') {
    particles.delete('motes');
    particles.add('ash');
  }
  const fog = mood === 'dread' ? Math.min(1, base.fog + 0.35) : mood === 'tense' ? Math.min(1, base.fog + 0.15) : base.fog;
  return { ...base, particles: [...particles].slice(0, 3), fog };
}
